/**
 * Reputation is the game's second currency.
 *
 * Research finding (see research/PLAYER_DEMAND.md): single-resource economies,
 * where money buys everything, are the root cause of the genre's late-game
 * collapse. Reputation exists to break that: it cannot be bought at any price,
 * only earned by doing work well, and it gates lead volume, pay rates, and
 * which equipment the shop will even sell you.
 */
import type { GameState } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import { BALANCE } from '../data/balance.js';

export class ReputationSystem {
  constructor(private readonly bus: EventBus) {}

  add(state: GameState, delta: number): void {
    if (delta === 0) return;
    const before = state.player.reputation;
    state.player.reputation = Math.max(
      0,
      Math.min(BALANCE.reputation.max, before + delta),
    );
    const applied = state.player.reputation - before;
    if (applied !== 0) {
      this.bus.emit({
        type: 'reputation.changed',
        delta: applied,
        value: state.player.reputation,
      });
    }
  }

  /**
   * Called once per completed job, scaled by how well it was done.
   *
   * Gains diminish as reputation rises: impressing a neighbourhood that has
   * never heard of you is easy, and the last few points are slow. Losses do
   * NOT diminish - a bad job hurts a good reputation more than a poor one,
   * which is both true to life and what stops high reputation from becoming a
   * state the player can stop maintaining.
   *
   * @param goodBar the standard this job is judged against. Defaults to the
   *   global threshold, but a job for a particular customer is judged by
   *   *their* standard - reputation and their satisfaction have to move
   *   together, or a delighted customer can still cost you reputation, which
   *   is incoherent from every angle the player can see.
   */
  applyJobResult(
    state: GameState,
    quality: number,
    goodBar: number = BALANCE.yard.qualityGoodThreshold,
  ): void {
    const cfg = BALANCE.reputation;
    if (quality < goodBar) {
      this.add(state, cfg.perPoorJob);
      return;
    }
    const excellentBar =
      goodBar + (BALANCE.yard.tipQualityThreshold - BALANCE.yard.qualityGoodThreshold);
    const base = quality >= excellentBar ? cfg.perExcellentJob : cfg.perGoodJob;
    const headroom = 1 - state.player.reputation / cfg.max;
    this.add(state, base * headroom * cfg.diminishingFactor);
  }

  applyWeeklyDecay(state: GameState): void {
    this.add(state, -BALANCE.reputation.weeklyDecay);
  }

  /** Fractional pay bonus from reputation, capped. */
  payMultiplier(state: GameState): number {
    const bonus = Math.min(
      BALANCE.yard.maxReputationPayBonus,
      state.player.reputation * BALANCE.yard.payPerReputation,
    );
    return 1 + bonus;
  }
}
